import { useForm } from "@mantine/form";
import { Button, TextInput } from "@mantine/core";
import { updateNotification } from "../../utils/useNotification";
import { updateEmployeeById } from "../../api/employeeApi";
import {
  combineValidators,
  validateMaxLength,
  validateMinLength,
  validateNoSpaces,
  validatePhoneNumberIsNumeric,
  validatePhoneNumberStartsWithZero,
  validateRequired,
} from "../../utils/validations";
import CustomSelect from "../common/Inputs/CustomSelect";
import CustomDatePicker from "../common/Inputs/CustomDatePicker";
import { useAuth } from "../../context/AuthContext";
import dayjs from "dayjs";
import { CustomMultiSelect } from "../common/Inputs/MultiSelectInput";
import { denormalizePhone } from "../../utils/phoneUtils";

const EmployeesUpdateForm = ({
  user,
  closeModal,
}: {
  user: any;
  closeModal: any;
}) => {
  const {
    roleSelectData,
    skillSelectData,
    departmentSelectData,
    contractSelectData,
  } = useAuth();

  const form = useForm({
    initialValues: {
      name: user?.name || "",
      phone: denormalizePhone(user?.phone || ""),
      departmentIds: user?.departments?.map((d: any) => d.value) || [],
      roleIds: user?.roles?.map((r: any) => r.value) || [],
      contractId: user?.contract?.value || null,
      assignedSkillIds: user?.assignedSkills?.map((s: any) => s.value) || [],
      hireDate: user?.hireDate ? dayjs(user.hireDate).toDate() : null,
      isWorkingCurrently: user?.isWorkingCurrently ?? true,
      birthday: user?.birthday || "",
    },
    validate: {
      name: validateRequired,
      phone: combineValidators(
        validateRequired,
        validatePhoneNumberStartsWithZero,
        validatePhoneNumberIsNumeric,
        validateNoSpaces,
        validateMinLength(11),
        validateMaxLength(11)
      ),
    },
  });

  const handleSubmit = async (values: object) => {
    try {
      const { data }: any = await updateEmployeeById({
        id: user?._id,
        updates: values,
      });
      updateNotification(data?.success);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      closeModal();
    }
  };

  return (
    <form
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "15px",
      }}
      onSubmit={form.onSubmit(handleSubmit)}
    >
      <TextInput
        label="Ad Soyad"
        placeholder="Ad Soyad Giriniz."
        radius={10}
        maxLength={40}
        size="md"
        {...form.getInputProps("name")}
      />
      <TextInput
        label="Telefon"
        placeholder="05*********"
        radius={10}
        size="md"
        type="tel"
        maxLength={11}
        {...form.getInputProps("phone")}
      />
      <CustomDatePicker
        label="İşe Giriş Tarihi"
        w="100%"
        {...form.getInputProps("hireDate")}
      />
      <CustomDatePicker
        label="Doğum Tarihi"
        placeholder="Tarih seçiniz."
        w="100%"
        {...form.getInputProps("birthday")}
      />
      <CustomMultiSelect
        label="Yetenek Seçiniz"
        placeholder="Aktif yeteneği yoktur."
        w="100%"
        {...form.getInputProps("assignedSkillIds")}
        data={skillSelectData}
      />
      <CustomMultiSelect
        label="Rol Seçiniz"
        w="100%"
        {...form.getInputProps("roleIds")}
        data={roleSelectData}
      />
      <CustomMultiSelect
        label="Departman Seçiniz"
        w="100%"
        {...form.getInputProps("departmentIds")}
        data={departmentSelectData}
      />
      <CustomSelect
        label="Kontrat Seçiniz"
        w="100%"
        {...form.getInputProps("contractId")}
        data={contractSelectData}
      />
      <CustomSelect
        label="Çalışma Durumu"
        w="100%"
        value={form.values.isWorkingCurrently?.toString()} // boolean -> string
        onChange={
          (val: any) => form.setFieldValue("isWorkingCurrently", val === "true") // string -> boolean
        }
        data={[
          { value: "true", label: "Çalışıyor" },
          { value: "false", label: "Ayrıldı" },
        ]}
      />

      <Button
        type="submit"
        color="#5C26FF"
        radius={10}
        disabled={!form.isDirty()}
      >
        Güncelle
      </Button>
    </form>
  );
};

export default EmployeesUpdateForm;
